import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Play, Pause, RefreshCw, AlertCircle, CheckCircle2, Search as SearchIcon } from "lucide-react";
import api from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { toast } from "sonner";

const pct = (v) => (v === null || v === undefined ? "—" : `${(v * 100).toFixed(0)}%`);
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");

const Rate = ({ label, rate, hits, total, previous, testid, split }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-2xl font-bold text-navy mt-1">{pct(rate)}</div>
    <div className="text-xs text-slate-500 mt-1">
      {total ? `${hits} of ${total} completed tests` : "No completed tests yet"}
      {previous !== null && previous !== undefined && total ? ` · ${pct(previous)} last month` : ""}
    </div>
    {split && (
      <div className="text-[11px] text-slate-500 mt-1.5 border-t border-slate-100 pt-1.5" data-testid={`${testid}-split`}>
        {split}
      </div>
    )}
  </div>
);

// Target and stretch never share a figure. A question naming nowhere is
// answered with national firms, so folding it into the same rate would bury
// progress on the questions the website can actually win.
export const ScopeFilter = ({ value, onChange, data }) => {
  const counts = Object.fromEntries((data?.by_scope || []).map((s) => [s.scope, s]));
  const label = (k) => {
    const c = counts[k];
    return c?.tests_completed ? ` (${c.tests_completed})` : "";
  };
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid="ai-scope-filter"
            title="Applies to the overview, platform comparison, question matrix and trend"
            className="text-xs font-semibold border border-slate-200 rounded-full px-3 py-1.5 text-slate-600">
      <option value="">All questions</option>
      <option value="target">Target only{label("target")}</option>
      <option value="stretch">Stretch only{label("stretch")}</option>
    </select>
  );
};

export const AiVisibilityOverview = ({ data, onRun, onPause, busy }) => {
  if (!data) return <p className="text-sm text-slate-500">Loading…</p>;
  const u = data.unbranded || {};
  const p = data.unbranded_previous || {};
  const b = data.branded || {};
  const active = data.active_run;
  const stalled = data.interrupted_run;
  // A single blended rate hides the whole point of the labels: a target question
  // is winnable from the website and a stretch one is answered nationally. Under
  // "All questions" the headline therefore carries its own split rather than
  // presenting one number as if it meant something on its own.
  const scoped = Object.fromEntries((data.by_scope || []).map((s) => [s.scope, s]));
  const splitFor = (field) => {
    if (data.scope) return "";
    const t = scoped.target;
    const s = scoped.stretch;
    if (!t?.tests_completed && !s?.tests_completed) return "";
    return `target ${t?.[field] ?? 0} of ${t?.tests_completed ?? 0} · stretch ${s?.[field] ?? 0} of ${s?.tests_completed ?? 0}`;
  };

  return (
    <div className="space-y-4" data-testid="ai-overview">
      {data.scope && (
        <p className="text-xs font-semibold text-sky-700" data-testid="ai-scope-active">
          Showing {data.scope} questions only. Branded questions are always included in brand accuracy.
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Rate label="Appearance rate" rate={u.appearance_rate} hits={u.appeared} total={u.tests_completed}
              previous={p.appearance_rate} testid="ai-rate-appearance" split={splitFor("appeared")} />
        <Rate label="Recommendation rate" rate={u.recommendation_rate} hits={u.recommended} total={u.tests_completed}
              previous={p.recommendation_rate} testid="ai-rate-recommendation" split={splitFor("recommended")} />
        <Rate label="Citation rate" rate={u.citation_rate} hits={u.cited} total={u.tests_completed}
              previous={p.citation_rate} testid="ai-rate-citation" split={splitFor("cited")} />
        <Rate label="Brand accuracy" rate={b.accuracy_rate} hits={b.accurate} total={b.assessable}
              testid="ai-rate-accuracy" />
      </div>
      {!data.scope && (scoped.target?.tests_completed || scoped.stretch?.tests_completed) ? (
        <p className="text-xs text-amber-700" data-testid="ai-blend-warning">
          These three rates combine target and stretch questions. Read the split instead: a stretch question
          names no location and is answered with national firms, so averaging it with a target question
          flatters one and buries the other.
        </p>
      ) : null}

      <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm" data-testid="ai-run-status">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-slate-600">
          <span><strong className="text-navy">{data.platforms_automated}</strong> automated platform(s) · {data.platforms_manual} manual</span>
          <span>Spend this month <strong className="text-navy">£{(data.spend_this_month || 0).toFixed(2)}</strong> of £{(data.monthly_cap_gbp || 0).toFixed(0)}</span>
          <span>Last run {when(data.last_run?.finished_at)}</span>
          <span>{data.auto_run ? `Scheduled ${data.frequency}` : "Runs only when you press the button"}</span>
        </div>
        {active ? (
          <p className="mt-3 flex items-center gap-2 text-xs text-sky-700" data-testid="ai-run-active">
            <Loader2 size={14} className="animate-spin" />
            Running now — {active.completed} of {active.tests_planned} done, {active.failed} failed.
          </p>
        ) : null}
        {!active && stalled ? (
          <div className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-900" data-testid="ai-run-interrupted">
            <p className="flex items-start gap-2 font-semibold">
              <AlertCircle size={14} className="shrink-0 mt-px" />
              Stopped early — {stalled.completed} of {stalled.tests_planned} answered, {stalled.outstanding} still
              to ask.
            </p>
            <p className="mt-1">
              {stalled.stop_reason || "The run stopped before finishing."} Resuming asks only the outstanding
              questions, so nothing already paid for is bought twice.
            </p>
            {(stalled.failures_by_platform || []).length > 0 && (
              <ul className="mt-2 space-y-0.5">
                {stalled.failures_by_platform.map((f) => (
                  <li key={f.platform}>
                    <strong>{f.platform}:</strong> {f.count} failed — {f.example}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
        {data.last_run?.status === "stopped_at_cap" && (
          <p className="mt-3 flex items-start gap-2 text-xs text-amber-700" data-testid="ai-run-capped">
            <AlertCircle size={14} className="shrink-0 mt-px" /> {data.last_run.stop_reason}
          </p>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <Button size="sm" onClick={onRun} disabled={busy || !!active} data-testid="ai-run-now">
            {busy ? <Loader2 className="animate-spin mr-2" size={14} /> : <Play className="mr-2" size={14} />}
            {stalled && !active ? `Resume — ${stalled.outstanding} left to ask` : "Run the question set now"}
          </Button>
          <Button size="sm" variant="outline" onClick={onPause} disabled={busy} data-testid="ai-pause">
            <Pause className="mr-2" size={14} /> {data.paused ? "Resume monitoring" : "Pause monitoring"}
          </Button>
        </div>
      </div>

      <p className="text-xs text-slate-500">{data.basis}</p>
      {data.run_completion && (
        <div className={`rounded-xl border p-4 text-xs ${data.run_completion.comparable
          ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-amber-200 bg-amber-50 text-amber-900"}`}
             data-testid="ai-run-completion">
          <p className="font-semibold">
            {data.run_completion.recorded === false ? "Completion not recorded"
              : data.run_completion.comparable ? "Complete run" : "Incomplete run"} ·{" "}
            {data.run_completion.repeats} response{data.run_completion.repeats === 1 ? "" : "s"} per question
            {data.run_completion.recorded !== false && (
              <> · {(data.run_completion.platforms_completed || []).join(", ") || "no platform finished"} answered
                every test</>
            )}
          </p>
          {(data.run_completion.platforms_partial || []).length > 0 && (
            <ul className="mt-1 space-y-0.5">
              {data.run_completion.platforms_partial.map((p) => (
                <li key={p.platform} data-testid={`ai-partial-${p.platform.toLowerCase()}`}>
                  {p.platform} answered {p.answered} of {p.expected}
                </li>
              ))}
            </ul>
          )}
          {(data.run_completion.platforms_missing || []).length > 0 && (
            <p className="mt-1">Not included in the run: {data.run_completion.platforms_missing.join(", ")}.</p>
          )}
          <p className="mt-1">{data.run_completion.note}</p>
        </div>
      )}
      {(data.by_scope || []).some((s) => s.tests_completed > 0) && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-xs" data-testid="ai-scope-split">
          <p className="font-semibold text-navy">Target against stretch</p>
          <ul className="mt-1.5 space-y-0.5 text-slate-600">
            {data.by_scope.map((s) => (
              <li key={s.scope} data-testid={`ai-scope-${s.scope}`}>
                <strong className="capitalize">{s.scope}</strong>: appeared in {s.appeared} of{" "}
                {s.tests_completed} completed tests{s.tests_completed ? ` (${pct(s.appearance_rate)})` : ""},
                recommended in {s.recommended}.
              </li>
            ))}
          </ul>
          <p className="mt-1.5 text-slate-500">
            Target questions name a place CASS serves. Stretch questions name nowhere, and are answered
            nationally — they are tracked, never averaged in.
          </p>
        </div>
      )}
      {u.tests_failed > 0 && (
        <p className="text-xs text-amber-700" data-testid="ai-failed-note">
          {u.tests_failed} test(s) failed and are excluded from every rate above — a failure is not a "no".
        </p>
      )}
    </div>
  );
};

// The Phase 0 baseline: the AI run and the Search Console figures, captured on
// the same day so the two halves describe the same week. Stored rather than
// recomputed — a baseline that moves with the 90-day window is not a baseline.
export const BaselinePanel = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    api.get("/admin/ai-monitor/baseline")
      .then((r) => setData(r.data))
      .catch((e) => {
        // Not setting an error here left the panel saying "Loading…" for ever,
        // which reads as a slow page rather than a failed request.
        setData({ failed: true, baselines: [], queries_tracked: [], pages_tracked: [] });
        setError(e?.response?.data?.detail || "The baseline could not be loaded.");
      });
  };
  useEffect(load, []);

  const capture = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await api.post("/admin/ai-monitor/baseline", {});
      toast.success(res.data.detail);
      load();
    } catch (e) {
      const why = e?.response?.data?.detail || "The baseline could not be captured.";
      setError(why);
      toast.error(why);
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <p className="text-sm text-slate-500">Loading…</p>;
  const latest = data.latest;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="ai-baseline">
      <div>
        <h3 className="font-bold text-navy">Phase 0 baseline</h3>
        <p className="text-xs text-slate-500 mt-1">{data.note}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={capture} disabled={busy} data-testid="ai-baseline-capture">
          {busy ? <Loader2 className="animate-spin mr-2" size={14} /> : <SearchIcon className="mr-2" size={14} />}
          Capture the Search Console half now
        </Button>
        <span className="text-xs text-slate-500">
          {data.queries_tracked.length} searches and {data.pages_tracked.length} principal pages
        </span>
      </div>
      {error && <p className="text-xs rounded-lg bg-red-50 px-3 py-2 text-red-700" data-testid="ai-baseline-error">{error}</p>}

      {!latest ? (
        <div data-testid="ai-baseline-empty">
          <p className="text-sm text-slate-600">
            No baseline recorded yet. Run the question set first, then capture this on the same day.
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 text-xs text-slate-600">
            <div>
              <p className="font-semibold text-navy">Searches tracked</p>
              <ul className="mt-1 space-y-0.5">
                {data.queries_tracked.map((q) => <li key={q}>{q}</li>)}
              </ul>
            </div>
            <div>
              <p className="font-semibold text-navy">Principal pages tracked</p>
              <ul className="mt-1 space-y-0.5 max-h-64 overflow-y-auto">
                {data.pages_tracked.map((p) => <li key={p.path}>{p.path}</li>)}
              </ul>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="ai-baseline-latest">
          <p className="text-xs text-slate-600">
            <strong className="text-navy">{latest.label}</strong> · {latest.window.start} to {latest.window.end} ·{" "}
            {latest.run
              ? `AI run of ${latest.run.repeats} response(s) per question on ${latest.run.platforms.join(", ")}`
              : "no AI run attached yet"}
            {latest.run && !latest.run.comparable && (
              <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">
                incomplete — excluded from trend comparisons
              </span>
            )}
          </p>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-slate-600"><tr>
                <th className="text-left py-1">Search</th><th className="text-left py-1">Clicks</th>
                <th className="text-left py-1">Impressions</th><th className="text-left py-1">Avg position</th>
                <th className="text-left py-1">In the brief</th>
              </tr></thead>
              <tbody>
                {latest.queries.map((q) => (
                  <tr key={q.query} className="border-t border-slate-100" data-testid={`ai-baseline-query-${q.query.replace(/\s+/g, "-")}`}>
                    <td className="py-1.5 pr-3">{q.query}</td>
                    <td className="py-1.5 pr-3">{q.found ? q.clicks : "no data"}</td>
                    <td className="py-1.5 pr-3">{q.found ? q.impressions : "—"}</td>
                    <td className="py-1.5 pr-3">{q.position ?? "—"}</td>
                    <td className="py-1.5 pr-3 text-slate-500">
                      {q.brief_october_2026.clicks} clicks, {q.brief_october_2026.impressions} impressions,
                      position {q.brief_october_2026.position}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-slate-600"><tr>
                <th className="text-left py-1">Principal page</th><th className="text-left py-1">Clicks</th>
                <th className="text-left py-1">Impressions</th><th className="text-left py-1">Avg position</th>
              </tr></thead>
              <tbody>
                {latest.pages.map((p) => (
                  <tr key={p.path} className="border-t border-slate-100">
                    <td className="py-1.5 pr-3">
                      {p.path}
                      <span className="block text-[11px] text-slate-400">{p.topic}</span>
                    </td>
                    <td className="py-1.5 pr-3">{p.found ? p.clicks : "no data"}</td>
                    <td className="py-1.5 pr-3">{p.found ? p.impressions : "—"}</td>
                    <td className="py-1.5 pr-3">{p.position ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-slate-400">
            Search Console omits low-volume and anonymised queries, so "no data" means it reported nothing for
            that page in this window — not that the page received nothing.
          </p>
        </div>
      )}
    </section>
  );
};

const STATE_LABEL = {
  answered: { text: "Answered", cls: "bg-emerald-100 text-emerald-700" },
  not_asked: { text: "Not yet asked", cls: "bg-slate-100 text-slate-600" },
  awaiting_key: { text: "Awaiting API key", cls: "bg-amber-100 text-amber-800" },
  manual: { text: "Manual only", cls: "bg-slate-100 text-slate-600" },
};

// Side by side, one column per platform. A platform with no answers shows no
// rate at all rather than 0% — nothing asked is not the same as CASS being
// left out, and the two would otherwise read identically.
const PlatformColumn = ({ p }) => {
  const badge = STATE_LABEL[p.state] || STATE_LABEL.not_asked;
  const answered = p.state === "answered";
  return (
    <div className={`rounded-xl border p-4 ${answered ? "border-slate-200 bg-white" : "border-dashed border-slate-200 bg-slate-50"}`}
         data-testid={`ai-compare-${p.platform.toLowerCase()}`}>
      <div className="flex items-start justify-between gap-2">
        <h5 className="font-bold text-navy">{p.platform}</h5>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${badge.cls}`}>{badge.text}</span>
      </div>
      <p className="text-[11px] text-slate-500 mt-1 break-words">{p.model || p.note}</p>

      {answered ? (
        <>
          <dl className="mt-3 space-y-2">
            {[["Appeared", p.appearance_rate, p.appeared],
              ["Recommended", p.recommendation_rate, p.recommended],
              ["Site cited", p.citation_rate, p.cited]].map(([label, rate, hits]) => (
              <div key={label}>
                <div className="flex items-baseline justify-between">
                  <dt className="text-xs text-slate-500">{label}</dt>
                  <dd className="text-lg font-bold text-navy">{pct(rate)}</dd>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-sky-500 transition-[width] duration-500"
                       style={{ width: `${Math.round((rate || 0) * 100)}%` }} />
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5">{hits} of {p.tests_completed} tests</p>
              </div>
            ))}
          </dl>
          <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600 space-y-1">
            <p>Average position when recommended:{" "}
              <strong className="text-navy">
                {p.average_position ? `${p.average_position} of those ranked` : "not ranked in any answer"}
              </strong>
            </p>
            {p.branded_assessed > 0 && (
              <p>Branded answers judged accurate:{" "}
                <strong className="text-navy">{p.branded_accurate} of {p.branded_assessed}</strong>
              </p>
            )}
            {p.tests_failed > 0 && <p className="text-amber-700">{p.tests_failed} failed, excluded from every rate</p>}
            <p className="text-slate-400">Last asked {when(p.last_asked)}</p>
          </div>
          {p.top_competitors?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Named instead</p>
              <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
                {p.top_competitors.map((c) => (
                  <li key={c.name} className="flex justify-between gap-2">
                    <span className="truncate">{c.name}</span><span className="text-slate-400">{c.mentions}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : (
        <p className="text-xs text-slate-500 mt-3">
          {p.state === "awaiting_key"
            ? "Add this platform's API key in Question Library & Scheduling, then include it in a run."
            : p.state === "manual"
              ? "No API exists, so this platform is only covered by the hand-run checks."
              : "Ready to run — no answers recorded this month, so there is nothing to rate yet."}
        </p>
      )}
    </div>
  );
};

export const PlatformComparison = ({ data }) => {
  const rows = data?.by_platform || [];
  const automated = rows.filter((p) => p.mode === "api");
  const manual = rows.filter((p) => p.mode !== "api");
  const answered = automated.filter((p) => p.state === "answered");
  const best = answered.reduce((a, p) =>
    (p.recommendation_rate ?? -1) > (a?.recommendation_rate ?? -1) ? p : a, null);

  return (
    <div data-testid="ai-platform-comparison">
      <p className="text-xs text-slate-500">
        {answered.length === 0
          ? "No platform has answered this month yet."
          : answered.length === 1
            ? `Only ${answered[0].platform} has answered this month, so there is nothing to compare it against yet.`
            : `${answered.length} platforms answered this month. ${
                best && best.recommendation_rate ? `${best.platform} recommends CASS most often (${pct(best.recommendation_rate)}).`
                  : "None of them recommended CASS in an unbranded answer."}`}
      </p>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {automated.map((p) => <PlatformColumn key={p.platform} p={p} />)}
      </div>

      {manual.length > 0 && (
        <p className="text-xs text-slate-500 mt-4">
          Manual only: {manual.map((p) => p.platform).join(", ")} — covered by the hand-run checks, never mixed
          into the figures above.
        </p>
      )}

      <p className="text-xs text-slate-500 mt-3">
        Unbranded questions only. An API answer is not the answer a person receives in the app, so the two
        sources are never added together, and a platform with no answers shows no rate rather than 0%.
      </p>
    </div>
  );
};

const CELL = {
  recommended: { mark: "✓", cls: "bg-emerald-100 text-emerald-800", title: "Recommended CASS" },
  appeared: { mark: "~", cls: "bg-amber-100 text-amber-800", title: "Mentioned CASS but did not recommend" },
  absent: { mark: "·", cls: "bg-slate-100 text-slate-400", title: "CASS not mentioned" },
  failed: { mark: "!", cls: "bg-red-100 text-red-700", title: "The request failed, so this is not a 'no'" },
  none: { mark: "", cls: "text-slate-300", title: "Not asked on this platform" },
};

// The same question, every platform, one row each. This is where a gap shows up
// as a gap: one platform naming CASS and another not is a content problem, not
// a measurement error.
export const QuestionMatrix = ({ data }) => {
  const platforms = (data?.by_platform || []).filter((p) => p.mode === "api" && p.state === "answered")
    .map((p) => p.platform);
  const rows = data?.by_question || [];
  if (platforms.length === 0 || rows.length === 0) {
    return <p className="text-sm text-slate-500" data-testid="ai-question-matrix-empty">
      No answers recorded this month yet, so there is nothing to line up side by side.
    </p>;
  }
  const classify = (cell) => {
    if (!cell) return CELL.none;
    if (cell.recommended > 0) return CELL.recommended;
    if (cell.appeared > 0) return CELL.appeared;
    if (cell.asked > 0) return CELL.absent;
    return cell.failed > 0 ? CELL.failed : CELL.none;
  };
  return (
    <div className="overflow-x-auto" data-testid="ai-question-matrix">
      <table className="min-w-full text-sm">
        <thead className="text-slate-600">
          <tr>
            <th className="text-left py-1 pr-3">Question</th>
            {platforms.map((p) => <th key={p} className="py-1 px-2 text-center font-semibold">{p}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((q) => (
            <tr key={q.question_id} className="border-t border-slate-100" data-testid={`ai-matrix-row-${q.question_id}`}>
              <td className="py-1.5 pr-3 text-slate-700">
                <span className="text-[11px] text-slate-400 mr-1">{q.category}</span>{q.question}
                <span className={`ml-2 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                  q.scope === "target" ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-500"}`}>
                  {q.scope}
                </span>
                {q.asked > 1 && (
                  <span className="block text-[11px] text-slate-500" data-testid={`ai-matrix-summary-${q.question_id}`}>
                    {q.summary}, across every platform asked
                  </span>
                )}
              </td>
              {platforms.map((p) => {
                const c = classify(q.platforms?.[p]);
                const cell = q.platforms?.[p];
                return (
                  <td key={p} className="py-1.5 px-2 text-center">
                    <span title={`${c.title}${cell?.asked > 1 ? ` — appeared in ${q.per_platform_summary?.[p]}` : ""}`}
                          className={`inline-flex h-6 w-6 items-center justify-center rounded-md text-xs font-bold ${c.cls}`}>
                      {c.mark}
                    </span>
                    {cell?.asked > 1 && (
                      <span className="block text-[10px] text-slate-400">{q.per_platform_summary?.[p]}</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-slate-500 mt-3">
        <span className="font-semibold">✓</span> recommended · <span className="font-semibold">~</span> mentioned
        only · <span className="font-semibold">·</span> not mentioned · <span className="font-semibold">!</span> request
        failed, which is never counted as CASS being left out.
      </p>
    </div>
  );
};

export const CategoryBreakdown = ({ data }) => (  <div className="overflow-x-auto" data-testid="ai-category-breakdown">
    <table className="min-w-full text-sm">
      <thead className="text-slate-600"><tr>
        <th className="text-left py-1">Question category</th><th className="text-left py-1">Appeared</th>
        <th className="text-left py-1">Recommended</th><th className="text-left py-1">Tests</th>
      </tr></thead>
      <tbody>
        {(data?.by_category || []).map((c) => (
          <tr key={c.category} className="border-t border-slate-100">
            <td className="py-1.5 pr-3">{c.category} · {c.label}</td>
            <td className="py-1.5 pr-3">{pct(c.appearance_rate)}</td>
            <td className="py-1.5 pr-3">{pct(c.recommendation_rate)}</td>
            <td className="py-1.5 pr-3 text-slate-500">{c.tests_completed}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Competitors = ({ data }) => (
  (data?.competitors || []).length === 0 ? (
    <p className="text-sm text-slate-600" data-testid="ai-competitors-empty">
      No other firms have been named in the answers recorded so far.
    </p>
  ) : (
    <div className="overflow-x-auto" data-testid="ai-competitors">
      <table className="min-w-full text-sm">
        <thead className="text-slate-600"><tr>
          <th className="text-left py-1">Firm named</th><th className="text-left py-1">Times recommended</th>
          <th className="text-left py-1">First seen</th><th className="text-left py-1">Last seen</th>
        </tr></thead>
        <tbody>
          {data.competitors.map((c) => (
            <tr key={c.key} className="border-t border-slate-100">
              <td className="py-1.5 pr-3 text-navy">{c.name}</td>
              <td className="py-1.5 pr-3">{c.mentions}</td>
              <td className="py-1.5 pr-3 text-slate-500">{when(c.first_seen)}</td>
              <td className="py-1.5 pr-3 text-slate-500">{when(c.last_seen)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
);

export const ResponseTable = ({ month }) => {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState({ only: "", platform: "" });
  const [open, setOpen] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    api.get("/admin/ai-monitor/responses", { params: { month, ...filter } })
      .then((r) => setRows(r.data.responses))
      .catch(() => setRows([]));
  }, [month, filter]);

  useEffect(() => { load(); }, [load]);

  const correct = async (id, fields) => {
    await api.patch("/admin/ai-monitor/responses", { id, ...fields });
    toast.success("Corrected. The original assessment is kept alongside it.");
    load();
  };

  if (!rows) return <p className="text-sm text-slate-500">Loading…</p>;
  const visible = rows.filter((r) => !search || r.question.toLowerCase().includes(search.toLowerCase())
    || (r.response_text || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="ai-responses">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative">
          <SearchIcon size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search questions and answers"
                 className="pl-8 w-64" data-testid="ai-response-search" />
        </div>
        {[["", "All"], ["appeared", "Appeared"], ["recommended", "Recommended"], ["failed", "Failed"]].map(([k, label]) => (
          <button key={k} onClick={() => setFilter((f) => ({ ...f, only: k }))} data-testid={`ai-filter-${k || "all"}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                    filter.only === k ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200"}`}>
            {label}
          </button>
        ))}
        <span className="text-xs text-slate-500">{visible.length} shown</span>
      </div>

      {visible.length === 0 ? (
        <p className="text-sm text-slate-600" data-testid="ai-responses-empty">
          Nothing recorded for this month yet. Press "Run the question set now" in the overview to collect the first set.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-slate-600"><tr>
              <th className="text-left py-1">Question</th><th className="text-left py-1">Platform</th>
              <th className="text-left py-1">Result</th><th className="text-left py-1">Asked</th><th />
            </tr></thead>
            <tbody>
              {visible.map((r) => (
                <React.Fragment key={r.id}>
                  <tr className="border-t border-slate-100 align-top">
                    <td className="py-1.5 pr-3 text-slate-700">
                      {r.question}
                      {r.branded && <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">branded</span>}
                    </td>
                    <td className="py-1.5 pr-3">{r.platform}</td>
                    <td className="py-1.5 pr-3">
                      {r.status === "failed" ? (
                        <span className="text-amber-700">failed — not counted</span>
                      ) : (
                        [r.appeared && "appeared", r.recommended && "recommended", r.cited && "cited",
                         r.accuracy && r.accuracy.replace("_", " ")].filter(Boolean).join(", ") || "not mentioned"
                      )}
                      {r.corrected && <span className="ml-2 text-[10px] font-semibold text-sky-700">corrected</span>}
                    </td>
                    <td className="py-1.5 pr-3 text-slate-500">{when(r.asked_at)}</td>
                    <td className="py-1.5 text-right">
                      <button onClick={() => setOpen(open === r.id ? null : r.id)} data-testid={`ai-response-open-${r.id}`}
                              className="text-xs font-semibold text-slate-500 hover:text-navy">
                        {open === r.id ? "Hide" : "Evidence"}
                      </button>
                    </td>
                  </tr>
                  {open === r.id && (
                    <tr className="bg-slate-50">
                      <td colSpan={5} className="p-4">
                        <div className="text-xs text-slate-500 mb-2">
                          {r.platform} · {r.model} · {r.grounding} · {r.mode === "api" ? "automated" : "manual"}
                          {r.repeat_index > 1 ? ` · repeat ${r.repeat_index}` : ""}
                        </div>
                        {r.error ? (
                          <p className="text-sm text-amber-700">{r.error}</p>
                        ) : (
                          <>
                            <pre className="whitespace-pre-wrap text-xs text-slate-700 max-h-80 overflow-y-auto">{r.response_text}</pre>
                            {(r.citations || []).length > 0 && (
                              <div className="mt-3 text-xs text-slate-600">
                                <strong>Cited:</strong>{" "}
                                {r.citations.map((c) => c.url).join(", ")}
                              </div>
                            )}
                            {r.accuracy_notes && <p className="mt-2 text-xs text-slate-700"><strong>Accuracy:</strong> {r.accuracy_notes}</p>}
                            {r.classifier_error && <p className="mt-2 text-xs text-amber-700">Classification unavailable: {r.classifier_error}</p>}
                            <div className="mt-3 flex flex-wrap gap-2">
                              <Button size="sm" variant="outline" onClick={() => correct(r.id, { recommended: !r.recommended })}
                                      data-testid={`ai-correct-recommended-${r.id}`}>
                                Mark as {r.recommended ? "not recommended" : "recommended"}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => correct(r.id, { appeared: !r.appeared })}
                                      data-testid={`ai-correct-appeared-${r.id}`}>
                                Mark as {r.appeared ? "not mentioned" : "mentioned"}
                              </Button>
                            </div>
                            {r.correction && (
                              <p className="mt-2 text-[11px] text-slate-500">
                                Corrected by {r.correction.by} on {when(r.correction.at)}. The original automatic
                                assessment is kept.
                              </p>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const Consistency = ({ data }) => (
  (data?.consistency || []).length === 0 ? (
    <p className="text-sm text-slate-600" data-testid="ai-consistency-empty">
      Each question has been asked once this month, so there is nothing to compare yet. Set repeats to 2 or 3 in
      the settings to measure how consistently CASS appears — answers vary between runs.
    </p>
  ) : (
    <div className="overflow-x-auto" data-testid="ai-consistency">
      <table className="min-w-full text-sm">
        <thead className="text-slate-600"><tr>
          <th className="text-left py-1">Question</th><th className="text-left py-1">Platform</th>
          <th className="text-left py-1">Appeared in</th>
        </tr></thead>
        <tbody>
          {data.consistency.map((c, i) => (
            <tr key={i} className="border-t border-slate-100">
              <td className="py-1.5 pr-3 text-slate-700">{c.question}</td>
              <td className="py-1.5 pr-3">{c.platform}</td>
              <td className="py-1.5 pr-3">{c.appeared} of {c.asked} runs ({pct(c.frequency)})</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
);

export const RefreshButton = ({ onClick, busy }) => (
  <button onClick={onClick} data-testid="ai-refresh"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-navy">
    <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
  </button>
);

export const Ok = ({ children }) => (
  <p className="flex items-start gap-2 text-xs text-emerald-600">
    <CheckCircle2 size={14} className="shrink-0 mt-px" /> <span>{children}</span>
  </p>
);
